import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { motion } from 'framer-motion';
import { toast } from 'react-toastify';
import styled from 'styled-components';
import { LuUser, LuFileText, LuEye, LuEyeOff } from 'react-icons/lu';
import { FaWhatsapp, FaApple } from 'react-icons/fa';

import { useAuth } from '../contexts/AuthContext';
import LoadingSpinner from '../components/LoadingSpinner';
import { Modal, ModalTitle, ModalBody, ModalFooter } from '../components/Modal';
import { getPlatform, shouldOfferAppDownload, APP_STORE_LINKS } from '../utils/platformUtils';

const SITUACOES_BLOQUEIO = ['cancelado', 'inadimplente', 'lead'];
const WHATSAPP_NUMBER = '552132798800';

const normalizeSituacao = (situacao) =>
  String(situacao || '').trim().toLowerCase();

const getSituacaoMessage = (situacao) => {
  switch (normalizeSituacao(situacao)) {
    case 'cancelado':
      return 'Identificamos que o contrato informado encontra-se cancelado. Por isso, não é possível acessar o portal neste momento.';
    case 'inadimplente':
      return 'Identificamos uma pendência financeira vinculada ao contrato. Por isso, não é possível acessar o portal neste momento.';
    case 'lead':
      return 'Ainda não encontramos um contrato ativo para os dados informados. Por isso, não é possível acessar o portal neste momento.';
    default:
      return 'Não foi possível realizar o login com os dados informados. Nossa equipe pode te ajudar a resolver isso.';
  }
};

// --- Styled Components (sem alterações) ---

const LoginContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%);
  display: flex;
  align-items: safe center;
  justify-content: safe center;
  padding: 1rem;
  position: relative;
  overflow-x: hidden;
  overflow-y: auto;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle at 30% 20%, rgba(30, 58, 138, 0.3) 0%, transparent 50%),
                radial-gradient(circle at 70% 80%, rgba(19, 85, 154, 0.2) 0%, transparent 50%);
    opacity: 0.6;
  }
`;

const LoginColumn = styled.div`
  width: 100%;
  max-width: 420px;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 14px;
  position: relative;
  z-index: 2;
`;

const LoginCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 24px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25), 
              0 0 0 1px rgba(255, 255, 255, 0.1);
  padding: 48px 40px 28px;
  width: 100%;
  position: relative;

  @media (max-width: 480px) {
    padding: 32px 24px 20px;
  }
`;

const LogoSection = styled.div`
  text-align: center;
  margin-bottom: 36px;

  img { 
    max-height: 100px; 
    margin-bottom: 16px; 
    @media (max-width: 480px) {
      max-height: 100px;
    }
  }
  
  h1 { 
    color: #1e3a8a;
    font-size: 26px; 
    font-weight: 700; 
    margin-bottom: 8px;
    @media (max-width: 480px) {
      font-size: 22px;
    }
  }
  
  p { 
    color: #64748b;
    font-size: 15px; 
    font-weight: 500;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const InputGroup = styled.div`
  position: relative;
`;

const InputLabel = styled.label`
  display: block;
  color: #1e293b;
  font-weight: 600;
  margin-bottom: 8px;
  font-size: 14px;
`;

const InputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const InputIcon = styled.div`
  position: absolute;
  left: 16px;
  color: #94a3b8;
  z-index: 1;
`;

const StyledInput = styled.input.withConfig({
  shouldForwardProp: (prop) => prop !== 'error' && prop !== '$error' && prop !== '$hasToggle',
})`
  width: 100%;
  padding: 18px ${props => props.$hasToggle ? '50px' : '16px'} 18px 50px;
  border: 2px solid #e2e8f0;
  border-radius: 14px;
  font-size: 16px;
  font-family: 'Montserrat', sans-serif;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  background: #ffffff;
  color: #1e293b;
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 4px rgba(19, 85, 154, 0.1);
  }
  
  ${props => props.$error && `
    border-color: #ef4444;
    box-shadow: 0 0 0 4px rgba(239, 68, 68, 0.1);
  `}

  @media (max-width: 480px) {
    padding-top: 16px;
    padding-bottom: 16px;
  }
`;

const TogglePasswordButton = styled.button`
  position: absolute;
  right: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 0;
  border: none;
  background: transparent;
  color: #94a3b8;
  cursor: pointer;
  z-index: 1;

  &:hover,
  &:focus {
    color: #1e3a8a;
    outline: none;
  }
`;

const ErrorMessage = styled.span`
  color: #ef4444;
  font-size: 14px;
  margin-top: 6px;
  display: block;
  font-weight: 500;
`;

const LoginButton = styled(motion.button)`
  background: linear-gradient(135deg, #3b82f6 0%, #1e3a8a 100%);
  color: #ffffff;
  border: none;
  padding: 20px 24px;
  border-radius: 14px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 4px 15px rgba(19, 85, 154, 0.3);
  
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }

  @media (max-width: 480px) {
    padding-top: 18px;
    padding-bottom: 18px;
  }
`;

const PlayIcon = () => (
  <svg width="22" height="24" viewBox="0 0 28 32" aria-hidden="true">
    <path fill="#4285F4" d="M.5 1.4C.2 1.7 0 2.2 0 2.8v26.4c0 .6.2 1.1.5 1.4l.1.1 14.8-14.8v-.3L.6 1.3.5 1.4z" />
    <path fill="#34A853" d="M20.3 20.6l-4.9-4.9v-.3l4.9-4.9.1.1 5.8 3.3c1.7 1 1.7 2.5 0 3.5l-5.8 3.3-.1-.1z" />
    <path fill="#FBBC04" d="M20.4 20.5L15.4 15.5.5 30.5c.5.5 1.3.6 2.2.1l17.7-10.1z" />
    <path fill="#EA4335" d="M20.4 11.5L2.7.4C1.8-.1 1 0 .5.5l14.9 15 5-4z" />
  </svg>
);

const StoreRow = styled.div`
  display: flex;
  justify-content: center;
  align-items: stretch;
  gap: 10px;
`;

const StoreBadge = styled.a`
  flex: 1 1 0;
  min-width: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 52px;
  padding: 6px 10px 6px 8px;
  background: #000000;
  color: #ffffff;
  border: 1px solid #3a3a3a;
  border-radius: 10px;
  text-decoration: none;
  transition: background 0.2s ease, border-color 0.2s ease;

  &:hover,
  &:focus,
  &:active,
  &:hover span,
  &:hover small,
  &:hover strong,
  &:focus span,
  &:focus small,
  &:focus strong {
    color: #ffffff;
    text-decoration: none;
  }

  &:hover {
    background: #1c1c1c;
    border-color: #ffffff;
  }

  span,
  small,
  strong {
    color: #ffffff;
    text-decoration: none;
  }

  span {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    line-height: 1;
    min-width: 0;
  }

  small {
    font-size: 8px;
    font-weight: 500;
    letter-spacing: 0.2px;
    white-space: nowrap;
  }

  strong {
    margin-top: 3px;
    font-size: 13px;
    font-weight: 600;
    letter-spacing: -0.3px;
    white-space: nowrap;
  }
`;

const AppDownloadBlock = styled.div`
  margin-top: 16px;
`;

const AppDivider = styled.hr`
  border: none;
  border-top: 1px solid #e2e8f0;
  margin: 0 0 14px;
`;

const SignUpLink = styled.div`
  text-align: center;
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid #e2e8f0;
  
  p {
    color: #64748b;
    font-size: 14px;
    margin-bottom: 8px;
  }
  
  a {
    color: #3b82f6;
    text-decoration: none;
    font-weight: 600;
    font-size: 15px;
    transition: color 0.3s ease;
    
    &:hover {
      color: #1e3a8a;
      text-decoration: underline;
    }
  }
`;

const ModalActions = styled(ModalFooter)`
  flex-direction: column;
  align-items: stretch;
  gap: 10px;

  @media (min-width: 480px) {
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: stretch;
  }
`;

const ModalActionButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
  padding: 14px 16px;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 600;
  text-decoration: none;
  text-align: center;
  transition: opacity 0.2s ease, transform 0.2s ease;
  border: none;
  cursor: pointer;
  font-family: 'Montserrat', sans-serif;
  color: #ffffff;

  &:hover,
  &:focus,
  &:active,
  &:visited {
    color: #ffffff;
    text-decoration: none;
    opacity: 0.92;
    transform: translateY(-1px);
  }
`;

const WhatsAppButton = styled(ModalActionButton)`
  background: #25d366;
  color: #ffffff;
`;

const HireButton = styled(ModalActionButton)`
  background: linear-gradient(135deg, #3b82f6 0%, #1e3a8a 100%);
  color: #ffffff;
`;

const Login = () => {
  const { login, loading, isAuthenticated, clearError } = useAuth();
  const navigate = useNavigate();
  const [situacaoModal, setSituacaoModal] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const { handleSubmit, control, setError, getValues } = useForm({
    mode: 'onBlur',
    defaultValues: { documento: '', contrato: '' }
  });

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    return () => clearError();
  }, [clearError]);

  const formatDocument = (value) => {
    if (!value) return '';
    const digits = value.replace(/\D/g, '');
    if (digits.length > 11) {
      return digits.substring(0, 14)
        .replace(/(\d{2})(\d)/, '$1.$2')
        .replace(/(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
        .replace(/(\d{2})\.(\d{3})\.(\d{3})(\d)/, '$1.$2.$3/$4')
        .replace(/(\d{2})\.(\d{3})\.(\d{3})\/(\d{4})(\d{1,2})/, '$1.$2.$3/$4-$5');
    }
    return digits.substring(0, 11)
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})\.(\d{3})(\d)/, '$1.$2.$3')
      .replace(/(\d{3})\.(\d{3})\.(\d{3})(\d{1,2})/, '$1.$2.$3-$4');
  };

  // --- LÓGICA DE SUBMISSÃO ATUALIZADA ---
  const onSubmit = async (data) => {
    setIsSubmitting(true);
    try {
      const result = await login(data.documento, data.contrato) || {};

      if (result.situacao && SITUACOES_BLOQUEIO.includes(normalizeSituacao(result.situacao))) {
        setSituacaoModal({
          situacao: result.situacao,
          contrato: data.contrato
        });
        return;
      }
      
      if (result.success) {
        if (result.contratos && result.contratos.length > 1) {
          navigate('/contracts'); 
        } else {
          navigate('/dashboard');
        }
      }
      else if (result.status === 401) {
        setError('documento', { type: 'manual', message: result.message });
        setError('contrato', { type: 'manual', message: '' });
      } 
      else if (result.status === 403 && result.message) {
        setError('documento', { type: 'manual', message: result.message });
        setError('contrato', { type: 'manual', message: '' });
      } 
      else if (result.isNetworkError) {
        setError('documento', { 
          type: 'manual', 
          message: 'Erro de conexão. Verifique sua internet.' 
        });
        setError('contrato', { type: 'manual', message: '' });
      }
      else if (result.message) {
        if (result.message.toLowerCase().includes('cpf') || result.message.toLowerCase().includes('cnpj')) {
          setError('documento', { type: 'manual', message: result.message });
        } else if (result.message.toLowerCase().includes('contrato')) {
          setError('contrato', { type: 'manual', message: result.message });
        } else {
          setError('documento', { type: 'manual', message: result.message });
        }
      }
    } catch (error) {
      console.error('Falha inesperada no processo de login:', error);
      if (!error.response) {
        toast.error('Erro de conexão. Verifique sua internet e tente novamente.', {
          autoClose: 6000
        });
        setError('documento', { 
          type: 'manual', 
          message: 'Erro de conexão. Verifique sua internet.' 
        });
      } else {
        toast.error('Ocorreu um erro inesperado. Tente novamente.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const getWhatsAppLink = (contrato) => {
    const mensagem = `Olá, meu contrato é ${contrato || getValues('contrato') || '___'} e não estou conseguindo logar no portal, poderia me auxiliar?`;
    return `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(mensagem)}`;
  };

  const cardVariants = { 
    hidden: { opacity: 0, y: 50 }, 
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } } 
  };

  // Função para obter a URL de vendas com parâmetro de origem
  const getVendasUrl = () => {
    const origem = getPlatform();
    return `https://vendas.santacasacopacabana.com.br/?origem=${origem}`;
  };
  
  const showAppDownload = shouldOfferAppDownload();

  // Spinner só na checagem inicial de sessão
  if (loading && !isSubmitting) return <LoadingSpinner />;

  return (
    <LoginContainer>
      <LoginColumn>
      <LoginCard variants={cardVariants} initial="hidden" animate="visible">
        <LogoSection>
          <img src="/logo2.png" alt="Santa Casa Copacabana" />
          <h1>Santa Casa Copacabana</h1>
          <p>Área do Cliente</p>
        </LogoSection>
        
        <Form onSubmit={handleSubmit(onSubmit)}>
          <InputGroup>
            <Controller
              name="documento"
              control={control}
              rules={{
                required: 'CPF/CNPJ é obrigatório',
                pattern: {
                  value: /(^\d{3}\.\d{3}\.\d{3}-\d{2}$)|(^\d{2}\.\d{3}\.\d{3}\/\d{4}-\d{2}$)/,
                  message: 'Formato de CPF/CNPJ inválido'
                }
              }}
              render={({ field: { onChange, onBlur, value, name, ref }, fieldState: { error } }) => (
                <>
                  <InputLabel htmlFor="documento">CPF / CNPJ</InputLabel>
                  <InputWrapper>
                    <InputIcon><LuUser /></InputIcon>
                    <StyledInput
                      id="documento"
                      autoComplete="username"
                      inputMode="numeric"
                      value={value ?? ''}
                      onChange={(e) => onChange(formatDocument(e.target.value))}
                      onBlur={onBlur}
                      name={name}
                      ref={ref}
                      placeholder="Digite seu CPF ou CNPJ"
                      $error={!!error}
                    />
                  </InputWrapper>
                  {error && <ErrorMessage>{error.message}</ErrorMessage>}
                </>
              )}
            />
          </InputGroup>

          <InputGroup>
             <Controller
                name="contrato"
                control={control}
                rules={{ required: 'Número do Contrato é obrigatório' }}
                render={({ field: { onChange, onBlur, value, name, ref }, fieldState: { error } }) => (
                    <>
                        <InputLabel htmlFor="contrato">Número do Contrato</InputLabel>
                        <InputWrapper>
                            <InputIcon><LuFileText /></InputIcon>
                            <StyledInput
                                id="contrato"
                                type={showPassword ? 'text' : 'password'}
                                autoComplete="current-password"
                                placeholder="Digite o número do contrato"
                                inputMode="numeric"
                                name={name}
                                value={value ?? ''}
                                onChange={onChange}
                                onBlur={onBlur}
                                ref={ref}
                                $error={!!error}
                                $hasToggle
                            />
                            <TogglePasswordButton
                                type="button"
                                onClick={() => setShowPassword((visible) => !visible)}
                                aria-label={showPassword ? 'Ocultar número do contrato' : 'Mostrar número do contrato'}
                                title={showPassword ? 'Ocultar' : 'Mostrar'}
                            >
                                {showPassword ? <LuEyeOff size={20} /> : <LuEye size={20} />}
                            </TogglePasswordButton>
                        </InputWrapper>
                        {error && <ErrorMessage>{error.message}</ErrorMessage>}
                    </>
                )}
            />
          </InputGroup>

          <LoginButton type="submit" disabled={isSubmitting} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
            {isSubmitting ? 'Entrando...' : 'Entrar'}
          </LoginButton>
        </Form>
        
        <SignUpLink>
          <p>Ainda não é cliente?</p>
          <a href={getVendasUrl()} target="_blank" rel="noopener noreferrer">
            Contrate aqui
          </a>
        </SignUpLink>

        {showAppDownload && (
          <AppDownloadBlock>
            <AppDivider />
            <StoreRow>
              <StoreBadge
                href={APP_STORE_LINKS.android.url}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={APP_STORE_LINKS.android.label}
              >
                <PlayIcon />
                <span>
                  <small>{APP_STORE_LINKS.android.caption}</small>
                  <strong>{APP_STORE_LINKS.android.name}</strong>
                </span>
              </StoreBadge>
              <StoreBadge
                href={APP_STORE_LINKS.ios.url}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={APP_STORE_LINKS.ios.label}
              >
                <FaApple size={24} />
                <span>
                  <small>{APP_STORE_LINKS.ios.caption}</small>
                  <strong>{APP_STORE_LINKS.ios.name}</strong>
                </span>
              </StoreBadge>
            </StoreRow>
          </AppDownloadBlock>
        )}
      </LoginCard>
      </LoginColumn>

      <Modal
        isOpen={!!situacaoModal}
        onClose={() => setSituacaoModal(null)}
      >
        <ModalTitle>Não foi possível entrar</ModalTitle>
        <ModalBody>
          <p>{getSituacaoMessage(situacaoModal?.situacao)}</p>
          <p style={{ marginTop: 12, marginBottom: 0 }}>
            Se precisar de ajuda, fale conosco pelo WhatsApp ou conheça nossos planos.
          </p>
        </ModalBody>
        <ModalActions>
          <WhatsAppButton
            href={getWhatsAppLink(situacaoModal?.contrato)}
            target="_blank"
            rel="noopener noreferrer"
          >
            <FaWhatsapp size={18} />
            Falar no WhatsApp
          </WhatsAppButton>
          <HireButton
            href={getVendasUrl()}
            target="_blank"
            rel="noopener noreferrer"
          >
            Contrate seu plano agora
          </HireButton>
        </ModalActions>
      </Modal>
    </LoginContainer>
  );
};

export default Login;
